import React, { CSSProperties, FC, useEffect, useRef, useState } from 'react'

import clsx from 'clsx'
import { Check } from 'lucide-react'
import ReactDOM from 'react-dom'

// The theme's own fixed bottom bars ("Start Your Order") are rendered by Liquid
// outside React, can be toggled per section, and differ in height between the
// mobile and desktop variants — so the clearance is measured the same way
// global.js measures it to pad the footer, rather than guessed per breakpoint.
const ACTION_BAR_SELECTOR = '.js-action-bar-mobile, .js-action-bar-desktop'

const measureActionBars = (): number => {
  let tallest = 0
  // offsetHeight is 0 while a bar is display:none, so hidden bars cost nothing.
  document.querySelectorAll<HTMLElement>(ACTION_BAR_SELECTOR).forEach((bar) => {
    tallest = Math.max(tallest, bar.offsetHeight)
  })

  return tallest
}

const useActionBarClearance = (): number => {
  const [clearance, setClearance] = useState(measureActionBars)

  useEffect(() => {
    const update = () => setClearance(measureActionBars())

    update()
    window.addEventListener('resize', update)

    return () => window.removeEventListener('resize', update)
  }, [])

  return clearance
}

interface Props {
  formId: string
  leaving: boolean
  onCancel(): void
  saved: boolean
  saving: boolean
}

export const SaveBar: FC<Props> = ({ formId, leaving, onCancel, saved, saving }) => {
  const clearance = useActionBarClearance()
  const barRef = useRef<HTMLDivElement>(null)
  const statusRef = useRef<HTMLParagraphElement>(null)

  // Saving drops the buttons, which would leave focus on <body> for anyone who
  // submitted from the keyboard. Hand it to the status line instead — it sits
  // in the same spot and reads out the result.
  useEffect(() => {
    if (!saved) return
    if (!barRef.current?.contains(document.activeElement)) return

    statusRef.current?.focus()
  }, [saved])

  return ReactDOM.createPortal(
    <div
      ref={barRef}
      aria-label="Profile changes"
      className={clsx(
        'user-profile-save-bar',
        leaving && 'user-profile-save-bar_leave',
        saved && 'user-profile-save-bar_saved',
      )}
      role="region"
      style={{ '--user-profile-save-bar-clearance': `${clearance}px` } as CSSProperties}
    >
      {/* The badge is a sibling of the message, not a child: the themes carry a
          global `p > :not(.btn)` reset that forces colour to inherit and blanks
          the background on anything nested inside a paragraph. */}
      <div className="user-profile-save-bar__message">
        {saved ? (
          <span aria-hidden="true" className="user-profile-save-bar__check">
            <Check size={13} strokeWidth={3} />
          </span>
        ) : null}
        <p ref={statusRef} aria-atomic="true" className="user-profile-save-bar__status" role="status" tabIndex={-1}>
          {saved ? 'Changes saved' : 'Unsaved changes'}
        </p>
      </div>

      {saved ? null : (
        <div className="user-profile-save-bar__actions">
          <button className="btn btn-transparent" disabled={saving} type="button" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn btn-accent" disabled={saving} form={formId} type="submit">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </div>,
    document.body,
  )
}
